<!--
  AI:
  Status: "confirmed"
  Type: Component
  SubType: ObjectBaseComponent
  Reason: Template for CiInsurableObjectObjectComponent - header with type icon and formatName plus
          the shared navigation rail with the Daten / Kunde / Adresse sections.
-->
@if (initialized && dto) {

    <div class="ci-insurable-object-object flex flex-column gap-3">

        <!-- Header: type icon + formatName -->
        <div class="flex align-items-center gap-3">
            <mvs-icon
                    [icon]="getTypeIcon()"
                    [class]="'text-2xl'"
                    [ngStyle]="getTypeIconStyle()">
            </mvs-icon>

            <div class="flex flex-column">
                <span class="text-lg font-semibold">{{ getObjectLabel() }}</span>

                @if (getTypeName()) {
                    <span class="text-sm text-gray-600">{{ getTypeName() }}</span>
                }
            </div>
        </div>

        <!--
          Shared icon rail (GC18). The items are not emitted upward, so a host that renders its own
          navigation sidebar cannot draw a second one.

          [syncUrlState] decides whether the rail owns the shared sideNavItem query parameter. On an
          object page it does. Hosted as a transient strip it must not, because a second rail is then
          on screen at the same time - see syncNavigationToUrl.
        -->
        <object-component-navigation-side
                [name]="getObjectLabel()"
                [navigationItems]="navigationItems"
                [syncUrlState]="syncNavigationToUrl"
                [(activeNavigationItem)]="activeNavigationItem">
        </object-component-navigation-side>

        <!-- ─────────────────────── Daten ─────────────────────── -->
        @if (isSectionActive('data')) {
            <!-- AI: CRUD Object Component for ci.InsurableObject (update, generic fields, suppressDelete) -->
            <mvs-crud-object
                    [objectType]="'ci.InsurableObject'"
                    [objectId]="getInsurableObjectId()"
                    [importObjectContextDto]="formImportObjectContext"
                    [removeDelete]="suppressDelete"
                    (onChangedObject)="handleChangedInsurableObject($event)">
            </mvs-crud-object>
        }

        <!-- ─────────────────────── Kunde ─────────────────────── -->
        @if (isSectionActive('customer')) {

            <div class="flex flex-column gap-3">

                @if (assignmentsError) {
                    <p-message severity="error" [text]="assignmentsError"></p-message>
                }

                @if (assignmentsBusy) {
                    <div class="flex flex-column gap-2">
                        @for (skeletonRow of skeletonRows; track skeletonRow) {
                            <p-skeleton height="2.5rem" borderRadius="0.5rem"></p-skeleton>
                        }
                    </div>
                }

                @if (!assignmentsBusy && !assignmentsError && assignments.length === 0) {
                    <div class="flex flex-column align-items-center gap-2 p-4 surface-50 border-round-lg">
                        <i class="fa-regular fa-user-slash text-2xl text-gray-600"></i>
                        <span class="text-gray-600">Dieses Objekt ist keinem Kunden zugeordnet.</span>
                    </div>
                }

                <!-- More than one assignment: choose first -->
                @if (!assignmentsBusy && showAssignmentList()) {
                    <div class="grid">
                        @for (assignment of assignments; track assignment.id) {
                            <div class="col-12 md:col-6">
                                <div
                                        class="ci-assignment-card h-full flex flex-column gap-1 p-3 surface-card border-1 border-300 border-round-lg"
                                        (click)="handleSelectAssignment(assignment)">

                                    <div class="flex align-items-center gap-2">
                                        <i class="fa-regular fa-user text-gray-600"></i>
                                        <span class="font-medium">{{ getAssignmentLabel(assignment) }}</span>
                                    </div>

                                    <span class="text-sm text-gray-600">
                                        {{ getAssignmentTypeLabel(assignment) }}
                                    </span>
                                    <span class="text-sm text-gray-600">
                                        Gültig ab {{ assignment.startDate | date: 'dd.MM.yyyy' }}
                                    </span>
                                </div>
                            </div>
                        }
                    </div>
                }

                <!-- The chosen (or only) assignment as an editable form -->
                @if (!assignmentsBusy && selectedAssignment) {

                    @if (assignments.length > 1) {
                        <p-button
                                label="Andere Zuordnung wählen"
                                icon="fa-regular fa-arrow-left"
                                severity="secondary"
                                [text]="true"
                                styleClass="align-self-start"
                                (onClick)="handleBackToAssignmentList()">
                        </p-button>
                    }

                    <!-- AI: CRUD Object Component for cr.CustomerInsurableObject (update) -->
                    <mvs-crud-object
                            [objectType]="'cr.CustomerInsurableObject'"
                            [objectId]="selectedAssignment.id"
                            [importObjectContextDto]="formImportObjectContext"
                            [removeDelete]="suppressDelete"
                            (onChangedObject)="handleChangedAssignment($event)">
                    </mvs-crud-object>
                }
            </div>
        }

        <!-- ────────────────────── Adresse ────────────────────── -->
        @if (isSectionActive('address') && getAddressId()) {

            <div class="flex flex-column gap-2">
                <span class="text-sm text-gray-600">
                    Objektspezifische Adresse - sie gehört zu diesem Objekt und nicht zum Kunden.
                </span>

                <!-- AI: CRUD Object Component for ad.Address (update) -->
                <mvs-crud-object
                        [objectType]="getAddressObjectType()"
                        [objectId]="getAddressId()"
                        [removeDelete]="suppressDelete"
                        (onChangedObject)="handleChangedAddress($event)">
                </mvs-crud-object>
            </div>
        }

    </div>
}
